import { Cross1Icon } from '@radix-ui/react-icons'
import React, { useEffect, useRef } from 'react'
import { Stack } from '../../../_shared/Stack'
import TertiaryButton from '../../../_shared/Buttons/TertiaryButton'
import { Filter } from '../../../icons/Filter'
import { MultiSelect } from '../../../_shared/MultiSelect'
import { mapCarStatus } from '../../../../../../../utils/helpers/helpers.cars'

export function FilterCarPark({
  cars,
  filters,
  setFilters,
}: {
  filters: any
  setFilters: (filters: any) => void
  cars: any[]
}) {
  const [showFilterBox, setShowFilterBox] = React.useState(false)

  return (
    <div className="relative">
      <button type="button" className="flex items-center gap-2" onClick={() => setShowFilterBox(true)}>
        Filtrer <Filter />
      </button>
      {showFilterBox && (
        <FilterBox
          cars={cars}
          showBox={showFilterBox}
          filters={filters}
          setFilters={setFilters}
          close={() => setShowFilterBox(false)}
        />
      )}
    </div>
  )
}

export function FilterBox({
  close,
  cars,
  filters,
  showBox,
  setFilters,
}: {
  close?: () => void
  cars: any
  showBox: boolean
  filters: any
  setFilters: any
}) {
  const statusOptions = cars.map((car) => car.status)
  const contactPerson = cars.map((car) => car.driverName)
  const invoiceGroup = cars.map((car) => car.invoiceGroup)
  const vehicleType = cars.map((car) => car.vehicleType)
  const consolidatedInvoiceCode = cars.map((car) => car?.consolidatedInvoiceCode)
  const vehicleMake = cars.map((car) => car.make.toUpperCase())
  const fuelType = cars.map((car) => car.fuel)
  const [tempFilters, setTempFilters] = React.useState(filters)
  const [openDropdownId, setOpenDropdownId] = React.useState<string | null>(null)

  // Collect all unique deviation/warning codes from cars
  const allDeviationCodes = Array.from(
    new Set(
      cars
        .flatMap((car) => [...(car.deviations || []), ...(car.warnings || [])])
        .map((d) => d.code)
        .filter(Boolean),
    ),
  )
  const filterDuplicates = (arr: string[]) => {
    const excludeNullAndEmptyStrings = arr?.filter((x): x is string => x !== '' && x !== null && x !== undefined)
    if (excludeNullAndEmptyStrings.length === 0) return []
    return Array.from(new Set(excludeNullAndEmptyStrings)).sort((a, b) => a.localeCompare(b))
  }
  const uniqueStatus = filterDuplicates(statusOptions)

  const uniqueContactPerson = filterDuplicates(contactPerson).filter((person) => person !== '')
  const uniqueInvoiceGroup = filterDuplicates(invoiceGroup)
  const uniqueVehicleType = filterDuplicates(vehicleType)
  const uniqueVehicleMake = filterDuplicates(vehicleMake)
  const uniqueFuelType = filterDuplicates(fuelType)
  const uniqueConsolidatedInvoiceCode = filterDuplicates(consolidatedInvoiceCode)

  function filterEverything() {
    setFilters(tempFilters)
    close()
  }

  useEffect(() => {
    setTempFilters(filters)
  }, [filters])

  function clearFilters() {
    const emptyArray = []
    const cleared = {
      status: emptyArray,
      contactPerson: emptyArray,
      invoiceGroup: emptyArray,
      vehicleType: emptyArray,
      consolidateInvoiceCode: emptyArray,
      vehicleMake: emptyArray,
      fuelType: emptyArray,
      deviationCodes: emptyArray,
    }
    setTempFilters(cleared)
    setFilters(cleared)
  }

  const boxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const isInsideBox = boxRef.current?.contains(target)
      const isDropdown = target.closest('[data-dropdown]')

      if (showBox && !isInsideBox && !isDropdown) {
        setOpenDropdownId(null)
        close?.()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showBox, close])

  function toggleMultiValue(current: string[] | undefined, value: string): string[] {
    return current?.includes(value) ? current.filter((v) => v !== value) : [...(current || []), value]
  }

  if (!showBox) return null
  return (
    <div
      ref={boxRef}
      className="fixed z-[9999] shadow-2xl px-6 py-8 expandFromRight h-screen bg-white border-[1px] border-slate-400 top-0 right-0 min-w-[300px] max-w-[340px] flex flex-col"
    >
      <div className="flex items-center w-full justify-between pb-8 shrink-0">
        <p>Filtrer Bilparken</p>
        <button type="button" onClick={() => close()}>
          <Cross1Icon />
        </button>
      </div>
      <Stack className="flex-1 min-h-0 overflow-y-auto pb-6" spacing="sm">
        <MultiSelect
          id="status"
          placeholder="Velg status..."
          isOpen={openDropdownId === 'status'}
          onOpenChange={(open) => setOpenDropdownId(open ? 'status' : null)}
          handleChange={(value) =>
            setTempFilters((prev) => ({
              ...prev,
              status: prev.status?.includes(value)
                ? prev?.status?.filter((s: string) => s !== value)
                : [...(prev.status || []), value],
            }))
          }
          options={uniqueStatus.map((status) => ({
            value: status,
            label: mapCarStatus(status),
            isChecked: tempFilters.status?.includes(status),
          }))}
          label="Status"
        />
        <MultiSelect
          id="consolidateInvoiceCode"
          placeholder="Velg type..."
          isOpen={openDropdownId === 'consolidateInvoiceCode'}
          onOpenChange={(open) => setOpenDropdownId(open ? 'consolidateInvoiceCode' : null)}
          handleChange={(value) =>
            setTempFilters((prev) => ({
              ...prev,
              consolidateInvoiceCode: prev.consolidateInvoiceCode?.includes(value)
                ? prev.consolidateInvoiceCode.filter((v: string) => v !== value)
                : [...(prev.consolidateInvoiceCode || []), value],
            }))
          }
          options={uniqueConsolidatedInvoiceCode.map((invoiceCode) => ({
            value: invoiceCode,
            label: invoiceCode,
            isChecked: tempFilters.consolidateInvoiceCode?.includes(invoiceCode),
          }))}
          label="Samlefakturatype"
        />

        <MultiSelect
          id="contactPerson"
          label="Kontaktperson"
          placeholder="Velg kontaktperson..."
          isOpen={openDropdownId === 'contactPerson'}
          onOpenChange={(open) => setOpenDropdownId(open ? 'contactPerson' : null)}
          handleChange={(value) =>
            setTempFilters((prev) => ({
              ...prev,
              contactPerson: toggleMultiValue(prev.contactPerson, value),
            }))
          }
          options={uniqueContactPerson.map((person) => ({
            value: person,
            label: person,
            isChecked: tempFilters.contactPerson?.includes(person),
          }))}
        />

        <MultiSelect
          id="invoiceGroup"
          label="Fakturagruppe"
          placeholder="Velg fakturagruppe..."
          isOpen={openDropdownId === 'invoiceGroup'}
          onOpenChange={(open) => setOpenDropdownId(open ? 'invoiceGroup' : null)}
          handleChange={(value) =>
            setTempFilters((prev) => ({
              ...prev,
              invoiceGroup: prev.invoiceGroup?.includes(value)
                ? prev.invoiceGroup.filter((v: string) => v !== value)
                : [...(prev.invoiceGroup || []), value],
            }))
          }
          options={uniqueInvoiceGroup.map((group) => ({
            value: group,
            label: group,
            isChecked: tempFilters.invoiceGroup?.includes(group),
          }))}
        />

        <MultiSelect
          id="vehicleType"
          label="Biltype"
          placeholder="Velg biltype..."
          isOpen={openDropdownId === 'vehicleType'}
          onOpenChange={(open) => setOpenDropdownId(open ? 'vehicleType' : null)}
          handleChange={(value) =>
            setTempFilters((prev) => ({
              ...prev,
              vehicleType: prev.vehicleType?.includes(value)
                ? prev.vehicleType.filter((v: string) => v !== value)
                : [...(prev.vehicleType || []), value],
            }))
          }
          options={uniqueVehicleType.map((type) => ({
            value: type,
            label: type,
            isChecked: tempFilters.vehicleType?.includes(type),
          }))}
        />

        <MultiSelect
          id="vehicleMake"
          label="Bilmerke"
          placeholder="Velg bilmerke..."
          isOpen={openDropdownId === 'vehicleMake'}
          onOpenChange={(open) => setOpenDropdownId(open ? 'vehicleMake' : null)}
          handleChange={(value) =>
            setTempFilters((prev) => ({
              ...prev,
              vehicleMake: prev.vehicleMake?.includes(value)
                ? prev.vehicleMake.filter((v: string) => v !== value)
                : [...(prev.vehicleMake || []), value],
            }))
          }
          options={uniqueVehicleMake.map((make) => ({
            value: make,
            label: make,
            isChecked: tempFilters.vehicleMake?.includes(make),
          }))}
        />

        <MultiSelect
          id="fuelType"
          label="Drivstoff"
          placeholder="Velg drivstoff..."
          isOpen={openDropdownId === 'fuelType'}
          onOpenChange={(open) => setOpenDropdownId(open ? 'fuelType' : null)}
          handleChange={(value) =>
            setTempFilters((prev) => ({
              ...prev,
              fuelType: prev.fuelType?.includes(value)
                ? prev.fuelType.filter((v: string) => v !== value)
                : [...(prev.fuelType || []), value],
            }))
          }
          options={uniqueFuelType.map((fuel) => ({
            value: fuel,
            label: fuel,
            isChecked: tempFilters.fuelType?.includes(fuel),
          }))}
        />

        <MultiSelect
          id="deviationCodes"
          label="Avvik / Varsler"
          placeholder="Velg avvik eller varsler..."
          isOpen={openDropdownId === 'deviationCodes'}
          onOpenChange={(open) => setOpenDropdownId(open ? 'deviationCodes' : null)}
          handleChange={(value) =>
            setTempFilters((prev) => ({
              ...prev,
              deviationCodes: prev.deviationCodes?.includes(value)
                ? prev.deviationCodes.filter((v: string) => v !== value)
                : [...(prev.deviationCodes || []), value],
            }))
          }
          options={allDeviationCodes.map((code) => ({
            value: String(code),
            label: String(code),
            isChecked: tempFilters.deviationCodes?.includes(String(code)),
          }))}
        />
      </Stack>
      <div className="flex gap-4 shrink-0 pt-4 border-t border-slate-200 bg-white">
        <button
          type="button"
          onClick={() => filterEverything()}
          className="px-4 py-2 bg-blue-600 rounded-md text-white"
        >
          Filtrer
        </button>
        <TertiaryButton onClick={() => clearFilters()}>Fjern filtrering</TertiaryButton>
      </div>
    </div>
  )
}
